// Casio FZ-1 / FZ-10M / FZ-20M FZF (Full Dump Data) Exporter
//
// FZF format (Casio "FZ-1 Data Structures", impl. jacobvosmaer/fz1):
//   [banks]   1024-byte sectors, bank name at +0x282           (up to 8, we use 0)
//   [voices]  256-byte descriptors, 4 per sector               (up to 64)
//               wavst(u32 @0)  waved(u32 @4)   wave start/end, WORD offsets into the pool
//               genst(u32 @8)  gened(u32 @12)  generated/loop sub-region
//               loop(u16 @0x10)  samplerate(u8 @0xb1)  name(12B @0xb2)
//   [PCM]     shared 16-bit LE word pool; a voice's audio is pool[wavst .. waved]
//
// Sample rate = 36000 / (1 << samplerate), samplerate ∈ {0,1,2} → 36k/18k/9k.
//
// The parser (casio-fz-parser.cjs) detects voices via _isFzVoice():
//   wavst <= genst && genst <= gened && gened <= waved &&
//   (loop === 0x01d7 || loop === 0x101d || loop === 0x2014 || loop === 0x0013)

export const FZF_SECTOR_SIZE = 1024;
export const FZF_VOICE_SIZE  = 256;    // each voice descriptor
export const FZF_MAX_VOICES  = 64;
export const FZF_MAX_BANKS   = 8;
export const FZF_FLOPPY_SIZE = 1310720; // 80 tracks × 2 sides × 8 sectors × 1024 bytes = 1.28 MB

// FZ-1 supported sample rates
export const FZF_SAMPLE_RATES = [
   { rate: 36000, code: 0 },
   { rate: 18000, code: 1 },
   { rate: 9000,  code: 2 },
];

// Loop mode values recognized by _isFzVoice()
const LOOP_NORMAL = 0x01d7; // 471 — normal one-shot playback

/**
 * Find the nearest FZ-1 supported sample rate.
 * Returns { rate, code }.
 */
export function nearestFzfRate(sr) {
   let best = FZF_SAMPLE_RATES[0];
   let bestDelta = Infinity;
   for (const r of FZF_SAMPLE_RATES) {
      const d = Math.abs(sr - r.rate);
      if (d < bestDelta) { bestDelta = d; best = r; }
   }
   return best;
}

/**
 * Build a 256-byte voice descriptor.
 *
 * @param {string} name - Voice name (max 12 chars)
 * @param {number} wavst - Wave start (word offset into PCM pool)
 * @param {number} waved - Wave end (word offset into PCM pool)
 * @param {number} rateCode - 0=36kHz, 1=18kHz, 2=9kHz
 * @returns {Uint8Array} 256-byte voice descriptor
 */
function buildVoiceDescriptor(name, wavst, waved, rateCode) {
   const v = new Uint8Array(FZF_VOICE_SIZE);
   const dv = new DataView(v.buffer);

   const numSamples = waved - wavst;

   // Wave start/end (u32 LE) — word offsets into PCM pool
   dv.setUint32(0, wavst, true);
   dv.setUint32(4, waved, true);
   // Gen start/end (u32 LE) — generated/loop sub-region
   dv.setUint32(8, wavst, true);
   dv.setUint32(12, Math.max(wavst, waved - 8), true);

   // Loop mode (u16 LE) = 471 (normal one-shot)
   dv.setUint16(16, LOOP_NORMAL, true);

   // Loop sus, loop end = 0
   dv.setUint16(18, 0, true);
   dv.setUint16(20, 0, true);

   // 8× loop start (u32 LE) = waved - 8
   const loopPt = Math.max(wavst, waved - 8);
   for (let i = 0; i < 8; i++) {
      dv.setUint32(22 + i * 4, loopPt, true);
   }
   // 8× loop end (u32 LE) = waved - 8
   for (let i = 0; i < 8; i++) {
      dv.setUint32(54 + i * 4, loopPt, true);
   }
   // 8× loop crossfade (u16 LE) = 0
   for (let i = 0; i < 8; i++) {
      dv.setUint16(86 + i * 2, 0, true);
   }
   // 8× loop time (u16 LE) = 100
   for (let i = 0; i < 8; i++) {
      dv.setUint16(102 + i * 2, 100, true);
   }

   // DCP (u16 LE) = 0
   dv.setUint16(118, 0, true);
   // DCF, DCQ = 0
   v[120] = 0; v[121] = 0;

   // DCA sustain = 0, DCA end = 7
   v[122] = 0;
   v[121] = 7; // wait, offset should be 123 for end
   // Let me be more careful with offsets based on wav2fzv script:
   // After loop time (8×u16 = 16 bytes, ending at 102+16=118):
   // Int(0) = DCP (u16 LE at 118)
   // Short(0) = DCF (u8 at 120) — wait, Short writes 1 byte
   // Actually looking at the awk script more carefully:
   // Short(x) = printf "%c", int(x)%256  — writes ONE byte
   // Int(x) = Short(x); Short(x/256) — writes TWO bytes (u16 LE)
   // Long(x) = Int(x); Int(x/65536) — writes FOUR bytes (u32 LE)

   // Let me recalculate from the wav2fzv script output:
   // Long(0)         → 4 bytes  → offset 0:   wave start
   // Long(samples)   → 4 bytes  → offset 4:   wave end
   // Long(0)         → 4 bytes  → offset 8:   gen start
   // Long(samples-8) → 4 bytes  → offset 12:  gen end
   // Int(471)        → 2 bytes  → offset 16:  loop mode
   // Short(0)        → 1 byte   → offset 18:  loop sus
   // Short(0)        → 1 byte   → offset 19:  loop end
   // 8× Long(samples-8) → 32 bytes → offset 20:  loop start ×8
   // 8× Long(samples-8) → 32 bytes → offset 52:  loop end ×8
   // 8× Int(0+crossfade) → 16 bytes → offset 84:  loop crossfade ×8
   // 8× Int(100)      → 16 bytes → offset 100: loop time ×8
   // Int(0)           → 2 bytes  → offset 116: DCP
   // Short(0)         → 1 byte   → offset 118: DCF
   // Short(0)         → 1 byte   → offset 119: DCQ
   // Short(0)         → 1 byte   → offset 120: DCA sustain
   // Short(7)         → 1 byte   → offset 121: DCA end
   // Short(127)       → 1 byte   → offset 122: DCA env rate 1
   // 7× Short(192)    → 7 bytes  → offset 123: DCA env rate 2-8
   // Short(255)       → 1 byte   → offset 130: DCA env stop 1
   // 7× Short(0)      → 7 bytes  → offset 131: DCA env stop 2-8
   // Short(0)         → 1 byte   → offset 138: DCF sustain
   // Short(7)         → 1 byte   → offset 139: DCF end
   // Short(127)       → 1 byte   → offset 140: DCF env rate 1
   // 7× Short(192)    → 7 bytes  → offset 141: DCF env rate 2-8
   // Short(255)       → 1 byte   → offset 148: DCF env stop 1
   // 7× Short(0)      → 7 bytes  → offset 149: DCF env stop 2-8
   // Int(0)           → 2 bytes  → offset 156: LFO delay
   // Short(128)       → 1 byte   → offset 158: LFO wave
   // Short(0)         → 1 byte   → offset 159: LFO attack
   // Short(64)        → 1 byte   → offset 160: LFO rate
   // Short(0)         → 1 byte   → offset 161: LFO DCP (vibrato)
   // Short(0)         → 1 byte   → offset 162: LFO DCA (tremolo)
   // Short(0)         → 1 byte   → offset 163: LFO DCF
   // Short(0)         → 1 byte   → offset 164: LFO DCQ
   // Short(0)         → 1 byte   → offset 165: vel_dcq_kf
   // Short(0)         → 1 byte   → offset 166: dca_kf
   // Short(0)         → 1 byte   → offset 167: dca_rs
   // Short(0)         → 1 byte   → offset 168: dcf_kf
   // Short(0)         → 1 byte   → offset 169: dcf_rs
   // Short(0)         → 1 byte   → offset 170: vel_dca_kf
   // Short(0)         → 1 byte   → offset 171: vel_dca_rs
   // Short(0)         → 1 byte   → offset 172: vel_dcf_kf
   // Short(0)         → 1 byte   → offset 173: vel_dcf_rs
   // Short(96)        → 1 byte   → offset 174: high width MIDI
   // Short(36)        → 1 byte   → offset 175: low width
   // Short(72)        → 1 byte   → offset 176: keynote center
   // Short(rate)      → 1 byte   → offset 177: sample rate code (0xB1)
   // printf name      → 12 bytes → offset 178: voice name (0xB2)
   // Short(0) Short(0) → 2 bytes → offset 190: padding
   // Total: 192 bytes of params + 64 bytes zero = 256 bytes

   // Reset and write properly:
   v.fill(0);

   // Offset 0: wave start (u32 LE)
   dv.setUint32(0, wavst, true);
   // Offset 4: wave end (u32 LE)
   dv.setUint32(4, waved, true);
   // Offset 8: gen start (u32 LE)
   dv.setUint32(8, wavst, true);
   // Offset 12: gen end (u32 LE) = waved - 8
   dv.setUint32(12, Math.max(wavst, waved - 8), true);
   // Offset 16: loop mode (u16 LE) = 471
   dv.setUint16(16, LOOP_NORMAL, true);
   // Offset 18: loop sus (u8) = 0
   v[18] = 0;
   // Offset 19: loop end (u8) = 0
   v[19] = 0;
   // Offset 20-51: 8× loop start (u32 LE) = waved - 8
   for (let i = 0; i < 8; i++) dv.setUint32(20 + i * 4, loopPt, true);
   // Offset 52-83: 8× loop end (u32 LE) = waved - 8
   for (let i = 0; i < 8; i++) dv.setUint32(52 + i * 4, loopPt, true);
   // Offset 84-99: 8× loop crossfade (u16 LE) = 0
   for (let i = 0; i < 8; i++) dv.setUint16(84 + i * 2, 0, true);
   // Offset 100-115: 8× loop time (u16 LE) = 100
   for (let i = 0; i < 8; i++) dv.setUint16(100 + i * 2, 100, true);
   // Offset 116: DCP (u16 LE) = 0
   dv.setUint16(116, 0, true);
   // Offset 118: DCF (u8) = 0
   v[118] = 0;
   // Offset 119: DCQ (u8) = 0
   v[119] = 0;
   // Offset 120: DCA sustain (u8) = 0
   v[120] = 0;
   // Offset 121: DCA end (u8) = 7
   v[121] = 7;
   // Offset 122: DCA env rate 1 (u8) = 127
   v[122] = 127;
   // Offset 123-129: DCA env rate 2-8 (u8) = 192
   for (let i = 0; i < 7; i++) v[123 + i] = 192;
   // Offset 130: DCA env stop 1 (u8) = 255
   v[130] = 255;
   // Offset 131-137: DCA env stop 2-8 (u8) = 0
   for (let i = 0; i < 7; i++) v[131 + i] = 0;
   // Offset 138: DCF sustain (u8) = 0
   v[138] = 0;
   // Offset 139: DCF end (u8) = 7
   v[139] = 7;
   // Offset 140: DCF env rate 1 (u8) = 127
   v[140] = 127;
   // Offset 141-147: DCF env rate 2-8 (u8) = 192
   for (let i = 0; i < 7; i++) v[141 + i] = 192;
   // Offset 148: DCF env stop 1 (u8) = 255
   v[148] = 255;
   // Offset 149-155: DCF env stop 2-8 (u8) = 0
   for (let i = 0; i < 7; i++) v[149 + i] = 0;
   // Offset 156: LFO delay (u16 LE) = 0
   dv.setUint16(156, 0, true);
   // Offset 158: LFO wave (u8) = 128
   v[158] = 128;
   // Offset 159: LFO attack (u8) = 0
   v[159] = 0;
   // Offset 160: LFO rate (u8) = 64
   v[160] = 64;
   // Offset 161-164: LFO DCP/DCA/DCF/DCQ (u8) = 0
   v[161] = 0; v[162] = 0; v[163] = 0; v[164] = 0;
   // Offset 165-173: velocity/keyboard follow (u8) = 0
   for (let i = 165; i < 174; i++) v[i] = 0;
   // Offset 174: high width MIDI (u8) = 96
   v[174] = 96;
   // Offset 175: low width (u8) = 36
   v[175] = 36;
   // Offset 176: keynote center (u8) = 72
   v[176] = 72;
   // Offset 177 (0xB1): sample rate code (u8) = rateCode
   v[177] = rateCode & 0xFF;
   // Offset 178-189 (0xB2-0xBD): voice name (12 ASCII, space-padded)
   const nameBytes = new TextEncoder().encode(name.toUpperCase().substring(0, 12));
   for (let i = 0; i < 12; i++) {
      v[178 + i] = i < nameBytes.length ? nameBytes[i] : 0x20; // space pad
   }
   // Offset 190-191: 2 zero bytes (already zeroed)
   // Offset 192-255: zero padding (already zeroed)

   return v;
}

/**
 * Calculate the FZF file size needed for a set of samples.
 *
 * @param {Array} samples - Array of { pcm: Int16Array }
 * @returns {number} - file size in bytes
 */
export function calcFzfCapacity(samples) {
   if (!samples.length) return 0;
   // Voice descriptors: 256 bytes each, padded to 1024-byte sectors
   const voiceBytes = samples.length * FZF_VOICE_SIZE;
   const voiceSectors = Math.ceil(voiceBytes / FZF_SECTOR_SIZE);
   // PCM pool: sum of all PCM data in bytes
   let pcmBytes = 0;
   for (const smp of samples) {
      if (smp.pcm && smp.pcm.length > 0) {
         pcmBytes += smp.pcm.length * 2;
      }
   }
   // Pad PCM to sector boundary
   const pcmSectors = Math.ceil(pcmBytes / FZF_SECTOR_SIZE);
   return (voiceSectors + pcmSectors) * FZF_SECTOR_SIZE;
}

/**
 * Build a genuine FZF (Full Dump Data) file containing multiple samples as voices.
 *
 * Layout:
 *   [voice descriptors]  256 bytes each, 4 per 1024-byte sector (0 banks)
 *   [PCM pool]           shared 16-bit LE words, each voice references by word offset
 *
 * @param {Array} samples - Array of { name, pcm: Int16Array, rateCode }
 * @param {object} opts - { name: dumpName (unused but kept for API compat) }
 * @returns {Uint8Array} FZF file data
 */
export function buildFzfDisk(samples, opts = {}) {
   const validSamples = samples.filter(s => s.pcm && s.pcm.length > 0).slice(0, FZF_MAX_VOICES);
   if (!validSamples.length) {
      return new Uint8Array(0);
   }

   // 1. Build the shared PCM pool and track word offsets
   let totalPcmWords = 0;
   for (const smp of validSamples) {
      totalPcmWords += smp.pcm.length;
   }
   const pcmBytes = totalPcmWords * 2;
   const pcmPool = new Uint8Array(pcmBytes);
   const pcmDv = new DataView(pcmPool.buffer);

   const voiceDescriptors = [];
   let wordOffset = 0;
   for (const smp of validSamples) {
      const wavst = wordOffset;
      const waved = wordOffset + smp.pcm.length;
      const rateCode = smp.rateCode !== undefined ? smp.rateCode : 0;
      const name = smp.name || `Voice ${voiceDescriptors.length + 1}`;

      // Write PCM into pool
      for (let i = 0; i < smp.pcm.length; i++) {
         pcmDv.setInt16(wordOffset * 2 + i * 2, smp.pcm[i], true);
      }
      wordOffset = waved;

      voiceDescriptors.push(buildVoiceDescriptor(name, wavst, waved, rateCode));
   }

   // 2. Pack voice descriptors into sectors (4 per 1024-byte sector)
   const numVoiceSectors = Math.ceil(voiceDescriptors.length * FZF_VOICE_SIZE / FZF_SECTOR_SIZE);
   const voiceRegionSize = numVoiceSectors * FZF_SECTOR_SIZE;
   const voiceRegion = new Uint8Array(voiceRegionSize);
   for (let i = 0; i < voiceDescriptors.length; i++) {
      voiceRegion.set(voiceDescriptors[i], i * FZF_VOICE_SIZE);
   }

   // 3. Pad PCM pool to sector boundary
   const pcmSectors = Math.ceil(pcmBytes / FZF_SECTOR_SIZE);
   const pcmRegionSize = pcmSectors * FZF_SECTOR_SIZE;
   const pcmRegion = new Uint8Array(pcmRegionSize);
   pcmRegion.set(pcmPool, 0);

   // 4. Concatenate: [voices][PCM pool]
   const fzf = new Uint8Array(voiceRegionSize + pcmRegionSize);
   fzf.set(voiceRegion, 0);
   fzf.set(pcmRegion, voiceRegionSize);

   console.log(`[FZF] buildFzfDisk: ${validSamples.length} voices, ${numVoiceSectors} voice sectors (${voiceRegionSize} bytes), ${pcmSectors} PCM sectors (${pcmRegionSize} bytes), total=${fzf.length} bytes`);
   for (let i = 0; i < validSamples.length; i++) {
      const smp = validSamples[i];
      console.log(`[FZF] voice ${i}: name="${smp.name}", pcm=${smp.pcm.length} samples, rateCode=${smp.rateCode}`);
   }

   return fzf;
}
